declare global {
  const dataLayer: any[];
  interface Window {
    dataLayer: typeof dataLayer;
  }
}

import { DOCUMENT, NgIf } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Renderer2, HostListener, ViewChild, ElementRef, ErrorHandler, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Event as RouterEvent, NavigationStart, NavigationEnd, Router, RouterOutlet, RouterLink } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';

import { Subscription, Subject, takeUntil } from 'rxjs';
import { tap, filter, map, mergeMap } from 'rxjs/operators';

import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';

// models
import { UserProfile } from './models';
import { HttpEvent } from './shared/events';
import UserSettings from './models/user-settings';

// services
import {
  AppSettingsService,
  DateService,
  ExitIntentService,
  LogService,
  FieldDataCollectionService,
  HelpService,
  ValueService,
  ErrorHelperService } from './services';
import { ClientStorageService } from './services/client-storage.service';
import { NotificationService } from './services/notification.service';
import { SystemService } from './services/system.service';
import { UserService } from './services/user.service';
import { SeasonalRentalService } from './listing/services/seasonal-rental.service';
import { StickyService } from './services/sticky.service';
import { MyMonitoringService } from './logging.service';
import { ErrorHandlerService } from './ErrorHandler.service';

// components
import { AppHeaderComponent } from './app-header.component';
import { AppFooterComponent } from './app-footer.component';
import { CollectEmailComponent } from './shared-components/collect-email/collect-email.component';
import { StickyButtonBarComponent } from './shared2-components/sticky-button-bar/sticky-button-bar.component';
import { StickyButtonComponent } from './common-components/sticky-button/sticky-button.component';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.less'],
    imports: [NgIf, AppHeaderComponent, RouterOutlet, StickyButtonComponent, AppFooterComponent, StickyButtonBarComponent, CollectEmailComponent],
    providers: [
        MyMonitoringService
    ]
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly _componentName = 'AppComponent';
  private _emailCollectPopupDelayMilliseconds: number;

  @ViewChild('collectEmailPopup', { static: true }) collectEmailPopup: ElementRef;
  @ViewChild('modal', { static: true }) modal: ElementRef;

  //isIframe = false;
  
  public isFacebookInAppBrowser = false;
  private _verboseLogging = false;
  public scrollStack = [];

  appStoreURL: string;
  stripeCustomerId: string;

  environment = {
    background: '',
    marker: '',
  };
  public modalRef: BsModalRef;

  public get emailCollectPopupDelayMilliseconds(): number {
    return this._emailCollectPopupDelayMilliseconds;
  }
  public set emailCollectPopupDelayMilliseconds(v: number) {
    this._emailCollectPopupDelayMilliseconds = v;
  }

  /**
   * Flag that is set by the subscription to the subject: SystemService.ApiAccessible
   * 0 = No, 1 = Yes, -1 = Not Set
   */
  public apiAccessible: number;

  private readonly _destroying$ = new Subject<void>();
  private routerEvent$: Subscription;

  private apiAccessibleSubscription: any;
  private userSettingsSubscription: any;
  private exitIntentSubscription: any;
  private routeQueryParamMapSubscription: any;

  private contactUsChatButtonButtonPress$: Subscription;

  private showUserPreferredActivityDialog: boolean = false;

  

  constructor(
    readonly route: ActivatedRoute,
    readonly router: Router,
    private meta: Meta,
    private renderer2: Renderer2,

    private modalService: BsModalService,

    readonly notificationService: NotificationService,
    readonly userService: UserService,
    public clientStorageService: ClientStorageService,
    
    private appSettingsService: AppSettingsService,
    private dateService: DateService,
    private systemService: SystemService,
    
    private seasonalLeaseService: SeasonalRentalService,
    private exitIntent: ExitIntentService,
    private log: LogService,
    private fieldDataService: FieldDataCollectionService,
    private helpService: HelpService,
    private valueService: ValueService,
    private stickyService: StickyService,
    private errorHelper: ErrorHelperService,

    private title: Title, 
    @Inject(DOCUMENT) private _document: Document,
    @Inject(PLATFORM_ID) private platformId: Object
    
  ) { }

  ngOnInit() {

    this.appSettingsService.load().subscribe();
      
    this.handleSpecialSituations();

    this.subscribeRouterEvents();

    this.configureContactUsChatButton();

    this.isFacebookInAppBrowser = this.detectFacebookInAppBrowser();

    this.checkLocationCookies(); // Check if location cookies exist on component load

    if (this.log.verboseLogging) {
      console.log(`${this._componentName}.ngOnInit()`);
    }

    
    this.userService.refresh();

    // update meta tags and title based on the current route data
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.route),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route) => route.data)
     )
     .subscribe((event) => {
        this.title.setTitle(event['title']);
        this.meta.updateTag({ name: 'description', content: event['description'] });

     }); 
      
     
    // Let the analytics service know that the website is loaded for the first time.
    // Facebook Pixel
    // fbq('track', 'ViewContent');

    this.showUserPreferredActivityDialog = false;

    // user/preferred-activity

    this.userSettingsSubscription = this.userService.getUserSettings().subscribe(
      (userSettings) => {
        if (this.verboseLogging) {
          console.log(`${this._componentName}.ngOnInit userService.getUserSettings.subscribe  userSettings= `, userSettings);
        }

        this.onNewUserSettings(userSettings);
      },
      (err) => {
        console.error(err);
        
        // this.notify.error('An subscription error occured',null);
      }
    );

    this.userService.clearCachedUserSettings();

    // Subscribe to login state changes to execute login-dependent logic
    this.userService.isLoggedIn$.pipe(
      takeUntil(this._destroying$)
    ).subscribe((isLoggedIn) => {
      if (isLoggedIn) {
        this.onUserLoggedIn();
      }
    });

    this.environment =
      this.getEnvironmentSettings(this.environment, 'stg') ||
      this.getEnvironmentSettings(this.environment, 'beta') ||
      this.getEnvironmentSettings(this.environment);

    this.routeQueryParamMapSubscription = this.route.queryParamMap.subscribe((p) => {
      if (p.get('previewShareId')) {
        localStorage.setItem('previewShareId', p.get('previewShareId'));
        this.exitIntent.disabled = true;
      } else {
        localStorage.setItem('previewShareId', null);
      }
    });

    /**
     * This exit intent subscribe is for mobile only. Because we are using ouibounce for the desktop exit intent.
     */
    this.exitIntentSubscription = this.exitIntent.exitIntent.subscribe(
      (data) => {
        if (this.log.verboseLogging) {
          console.log(`${this._componentName}.exitIntent.subscribe data=`, data);
        }

        //if (this.device.isMobile) {
          // The exit intent service handles the logic of what "defines" an exit intent.
          // Here, we just respond to it and show the email collect popup:

          this.showEmailCollectPopup();
        //}
      },
      (err) => {
        console.error(err);
      }
    );
    // Load up an initial count
    this.seasonalLeaseService.refreshListingSeasonalLeaseSummary();

    this.apiAccessibleSubscription = this.systemService.ApiAccessible.subscribe({
      next: (n) => {
        this.apiAccessible = n;
      },
      error: (err) => {
        this.apiAccessible = 0;
        
        console.error(err);
      }
    });

    this.appSettingsService.applicationStartDateTime = this.dateService.nowUTC;
    
    
  }

  
  // Function to check if location cookies exist and get user location if not
  checkLocationCookies(): void {
    const userCity = this.userService.getCookie("user_city");
    const userState = this.userService.getCookie("user_state");

    if (userCity && userState) {
      // Cookies exist, do nothing
      //console.log("Location cookies already set:", userCity, userState);
    } else {
      // Cookies don't exist, get user location
      this.getUserLocation();
    }
  }

  // Function to get user's location using Geolocation API
  getUserLocation(): void {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => this.onGeoSuccess(position),
        () => this.callIpAddrToLocation(),
        { timeout: 10000 }
      );
    } else {
      // If Geolocation API is not supported, fallback to IP-based location
      this.callIpAddrToLocation();
    }
  }

  // Success callback for Geolocation API
  onGeoSuccess(position: GeolocationPosition): void {
    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;

     // Call the latlongtolocation API
    this.appSettingsService.latLongToLocation(latitude, longitude).subscribe(
      (location) => {
        this.userService.setCookie("user_city", location.city, 1); // Store city and state in cookies for 1 hour
        this.userService.setCookie("user_state", location.state, 1);
      },
      (err) => {
        console.error("Error fetching location from latlongtolocation API:", err);
      }
    );
  }

  // Function to call IP-based location API
  callIpAddrToLocation(): void {
    this.appSettingsService.ipAddressToLocation().subscribe(
      (location) => {
        this.userService.setCookie("user_city", location.city, 1); // Store city and state in cookies for 1 hour
        this.userService.setCookie("user_state", location.state, 1);
      },
      (err) => {
        console.error("Error fetching location from ipaddrtolocation API:", err);
      }
    );
  }

  
  private configureContactUsChatButton() {
    this.contactUsChatButtonButtonPress$ = this.stickyService.buttonPress.subscribe({
      next: (name) => {
        console.log(`${this._componentName}.configureContactUsChatButton() stickyService.buttonPress subscription name=`, name);
        if (name == 'sticky-contact-us-button') {
          this.router.navigate(['/contact-us']);
        }
      },
      error: (err) => {
        let msg = this.errorHelper.getErrorMessage(err) || 'A subscription error occured';
        console.error(msg);
      }
    });
  }

  private handleSpecialSituations() {
    let params = new URLSearchParams(this._document.location.search);

    // Check for the 'terseCoypuWrench' parameter to disable exit intent and help service
    if (params.has('terseCoypuWrench')) {
      this.exitIntent.disabled = true;
      this.helpService.disabled = true;
    }

    // if we redirected away due to Stripe/Klarna - the redirect back needs to be handled here
    if (
      params.has("payment_intent") &&
      params.has("payment_intent_client_secret") &&
      !this._document.location.pathname.includes("/pmt-confirm-ret")
    ) {
      // Use Angular Router for navigation
      const queryParams: any = {
        payment_intent: params.get("payment_intent"),
        payment_intent_client_secret: params.get("payment_intent_client_secret"),
      };
      if (params.has("redirect_status")) {
        queryParams.redirect_status = params.get("redirect_status");
      }
      this.router.navigate(["/pmt-confirm-ret"], { queryParams });
      return; // Prevent further execution
    }

    // possibly redirect to landowner/profit or eoh/dwr based on p1v parameter
    if (params.has("p1v")) {
      let p1v = params.get("p1v");
      if (p1v && p1v.length > 0) {
        p1v = p1v.toLowerCase();
        if (p1v === 'nc' || p1v === 'aaa' || p1v === 'va' || p1v === 'sc' || p1v === 'ga' || p1v === 'tx' || p1v === 'gad' || p1v === 'land') {
          if (params.has('rdc') && params.has('v')) {
            this.router.navigate(['/landowner', 'profit'], { queryParams: { rdc: params.get("rdc"), v: params.get("v") } });
          } else if (params.has('rdc')) {
            this.router.navigate(['/landowner', 'profit'], { queryParams: { rdc: params.get("rdc") } });
          } else {
            this.router.navigate(['/landowner', 'profit']);
          }
        } else if (p1v === 'dwrmentor') {
          if (params.has('rdc')) {
            this.router.navigate(['/eoh','dwr'], { queryParams: { rdc: params.get("rdc") } });
          } else {
            this.router.navigate(['/eoh','dwr']);
          }
        }
      }
    }
  }


  /**
   * Gets the user canary value and stored in the neutralMarble session storage
   */
  private getCanary() {
    this.userService.getCanary().subscribe(
      (result) => {
        if (this.verboseLogging) {
          console.log(`${this._componentName} getCanary() subscription result=`, result);
        }

        if (result) {
          sessionStorage.setItem('neutralMarble', result.toString());
        }
      },
      (err) => {
        console.error(err);
      }
    );
  }

  /**
   * Executes initialization logic for logged-in users
   * This method is called whenever the user logs in (including on initial load)
   */
  private onUserLoggedIn(): void {
    console.log(`${this._componentName}.onUserLoggedIn() - User is now logged in`);
    
    // Save user's geo location
    this.userService.saveUserGeoLocation();

    // Refresh user data
    console.log(`${this._componentName}.onUserLoggedIn() - Refreshing membership tier`);
    this.userService.refreshMembershipTier();

    console.log(`${this._componentName}.onUserLoggedIn() - Refreshing listing favorites`);
    this.userService.refreshListingFavorites();

    // Get field data collection
    this.fieldDataService.getFieldDataCollection(false).subscribe();

    // Get canary value
    this.getCanary();

    // Get Stripe ID
    this.userService.getStripeId().subscribe((stripeId: string) => {
      this.stripeCustomerId = stripeId;
    });
  }

  
  get userProfile() {
    return this.userService.profile;
  }

  currentUrl = '';
  padUrl = false;
  readonly regex = /home|listing/gi;
  showFooter = true;
  sub: any;
  scriptText: string;
  tempProfile: any;

  ngOnDestroy() {
    this._destroying$.next(undefined);
    this._destroying$.complete();

    if (this.sub) {
      this.sub.unsubscribe();
    }

    if (this.routerEvent$) {
      this.routerEvent$.unsubscribe();
    }

    if (this.routeQueryParamMapSubscription) {
      this.routeQueryParamMapSubscription.unsubscribe();
    }

    if (this.exitIntentSubscription) {
      this.exitIntentSubscription.unsubscribe();
    }

    if (this.apiAccessibleSubscription) {
      this.apiAccessibleSubscription.unsubscribe();
    }

    if (this.userSettingsSubscription) {
      this.userSettingsSubscription.unsubscribe();
    }

    if (this.contactUsChatButtonButtonPress$) {
      this.contactUsChatButtonButtonPress$.unsubscribe();
    }
  }

  /**
   * Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
   * Add 'implements AfterViewInit' to the class.
   */
  // not currently until we find a replacement for Ouibounce (or write one ourselves)
  ngAfterViewInit(): void {
    // // Configure the exit intent/email collector
    // var self = this;

    // // Set the Email Collect Popup Display, for users who aren't logged in.
    // if (!this.userService.isLoggedIn) {
    //   this.appSettingsService.getAppSettings().subscribe((x) => {
    //     const emailCollected = localStorage.getItem('afterclapGiraffe') === 'true';

    //     if (!emailCollected) {
    //       // Configure the ouibounce exit-intent
    //       // sensitivity default is 20: sensitivity:20
    //       // viewedOuibounceModal is the default name of the cookie
    //       // delay - visitor grace period
    //       ouibounce(false, {
    //         aggressive: true,
    //         delay: 350,
    //         callback: function () {
    //           // console.log('ouibounce fired!');
    //           if (self.log.verboseLogging) console.log(`${self._componentName}.ouibounce-callback`);

    //           if (!self.exitIntent.disabled) self.showEmailCollectPopup();
    //         },
    //       });
    //     }
    //   });
    // }
  }

  @HostListener('window:beforeunload', ['$event'])
  public unloadNotification($event: any) {
    if (this.log.verboseLogging) console.log(`${this._componentName}.unloadNotification(any) event=`, $event);

    if (!this.userService.isLoggedIn) {
      // $event.returnValue =false;
      // $event.preventDefault();
      // $event.returnValue = '';
      // 
    }
  }

  @HostListener('window:scroll')
  public onWindowScroll() {
    //In chrome and some browser scroll is given to body tag
    let pos = (document.documentElement.scrollTop || document.body.scrollTop) + document.documentElement.offsetHeight;
    let max = document.documentElement.scrollHeight;

    this.exitIntent.add({
      timestamp: this.dateService.nowUTC,
      pos: pos,
    });

    if (this.log.verboseLogging) console.log(`${this._componentName}.onWindowScroll pos=${pos}, max=${max}`, this.scrollStack);
  }

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging || this._verboseLogging;
  }

  handleHttpError = (error: HttpEvent) => {
    if (error.status === 401 || error.status === 403) {
      console.info(`${error}\n`);
    } else if (error.status === 400) {
      // ignore 400 Bad Request, it is used by convention to wrap field validation and other request errors.
    } else {
      this.notificationService.error(error.statusText, null);
    }
  };

  getEnvironmentSettings({ background, marker }, env?: 'stg' | 'beta') {
    const backgrounds = {
      stg: '#009688',
      beta: '#e95846',
      prod: '#1e1e1e',
    };

    if (!env) {
      return {
        background: backgrounds.prod,
        marker: undefined,
      };
    }
    const {
      location: { href: url },
    } = window;
    if (url.includes(`oa-${env}`)) {
      return {
        background: background === backgrounds[env] ? background.prod : backgrounds[env],
        marker: marker === env ? 'prod' : env,
      };
    }
  }

  /**
   * Handles new user settings.  Checks to see if the user preferred dialog should be shown.
   * @param userSettings
   */
  private onNewUserSettings(userSettings: UserSettings) {
    console.log(`${this._componentName}.onNewUserSettings`);

    this.showUserPreferredActivityDialog = this.userService.showUserPreferredActivityDialog();

    //if (this.showUserPreferredActivityDialog) {
    //  this.router.navigate(['/user/preferred-activity']);
    //}
  }

  private setFriendBuyTrackingScript() {
    if (this.userService.userName) {
      this.userService.getProfile().subscribe((profile: UserProfile) => {
        this.scriptText = `window['friendbuy'] = window['friendbuy'] || [];
        window['friendbuy'].push(['site', 'site-b8d9b961-www.outdooraccess.com']);
        window['friendbuy'].push(['track', 'customer',
            {
                id: 'USERID', 
                email: 'USEREMAIL', 
                first_name: 'USERFIRSTNAME', 
                last_name: 'USERLASTNAME' ,
                stripe_customer_id: 'STRIPECUSTOMERID'
            }
        ]);
        (function (f, r, n, d, b, y) {
            b = f.createElement(r), y = f.getElementsByTagName(r)[0];b.async = 1;b.src = n;y.parentNode.insertBefore(b, y);
        })(document, 'script', '//djnf6e5yyirys.cloudfront.net/js/friendbuy.min.js');`;

        console.log('stripeId', this.stripeCustomerId);
        this.scriptText = this.scriptText.replace('USERID', this.userService.userId.toString());
        this.scriptText = this.scriptText.replace('USEREMAIL', this.userService.userName);
        this.scriptText = this.scriptText.replace('USERFIRSTNAME', profile.firstName);
        this.scriptText = this.scriptText.replace('USERLASTNAME', profile.lastName);
        this.scriptText = this.scriptText.replace('STRIPECUSTOMERID', this.stripeCustomerId);

        const s = this.renderer2.createElement('script');
        s.type = 'text/javascript';
        s.text = this.scriptText;
        this.renderer2.appendChild(this._document.body, s);
      });
    }
  }

  /**
   * Shows the email collect popup
   */
  private showEmailCollectPopup() {
    console.log(`${this._componentName}.showEmailCollectPopup()`);

    const emailCollected = localStorage.getItem('afterclapGiraffe') === 'true';

    if (!this.userService.isLoggedIn && !emailCollected && !this.exitIntent.disabled) {
      // let lastShown = localStorage.getItem("emailCollectPopupLastShown");
      // if (!lastShown) {
      // cookieExpire uom is days
      const config = {
        animated: true,
        class: 'modal-sm modal-dialog-centered',
        backdrop: false,
        cookieExpire: 10,
      };
      // @ts-ignore
      this.modalRef = this.modalService.show(this.collectEmailPopup, config);
      this.modalService.checkScrollbar();

      //   localStorage.setItem("emailCollectPopupLastShown",new Date().toISOString())
      // }
    }
  }

  /**
   *
   */
  public onCollectEmailComplete() {
    console.log(`${this._componentName}.onCollectEmailComplete()`);
    this.modalRef.hide();
  }

  private subscribeRouterEvents() {
    // check for routes with hashes in then redirect to the route without the hash
    // to help with the transition from HashLocationStrategy to PathLocationStrategy
    this.routerEvent$ = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart && event.url.includes('#')) {
        const [, hash] = event.url.split('#');
        if (!hash || hash.length <= 1) {
          this.router.navigate(['/']);
        } else {
          this.router.navigate([hash]);
        }
      }
      // initialize the verbose logging setting
      if (event instanceof NavigationEnd) {
        const verbose = this.route.snapshot.queryParamMap.get('verboseLogging');
        this._verboseLogging = this.route.snapshot.queryParamMap.has('verboseLogging')
          ? !!this.valueService.getBoolean(verbose)
          : this._verboseLogging;
      }
    });
  }

  loginRedirect() {
    this.userService.loginRedirect();
  }

  logout() {
    this.userService.logoutRedirect();
  }

  private detectFacebookInAppBrowser(): boolean {
    const ua = navigator.userAgent || navigator.vendor;
    // Facebook in-app browser user agent contains 'FBAN' or 'FBAV'
    return /FBAN|FBAV/i.test(ua);
  }

  
}
